import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';

const DynamicModal = ({
    show,
    onHide,
    title = "Modal Title",
    size = "md", // sm, md, lg, xl
    centered = true,
    backdrop = true,
    keyboard = true,
    children, // For custom content

    // Form fields configuration
    fields = [], // Array of field objects
    initialData = {},

    // Buttons configuration
    buttons = [
        { label: "Cancel", variant: "secondary", action: "close" },
        { label: "Save", variant: "primary", action: "submit" }
    ],

    // Callbacks
    onSubmit,
    onCancel,
    onFieldChange,

    // Loading states
    loading = false,

    // Additional props
    bodyClassName = "",
    headerClassName = "",
    footerClassName = ""
}) => {
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});

    // Update formData when initialData changes
    useEffect(() => {
        if (JSON.stringify(formData) !== JSON.stringify(initialData)) {
            setFormData(initialData);
        }
    }, [initialData]);

    // Handle input change
    const handleInputChange = (fieldKey, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldKey]: value
        }));

        // Clear error for this field
        if (errors[fieldKey]) {
            setErrors(prev => ({
                ...prev,
                [fieldKey]: null
            }));
        }

        // Call external onChange if provided
        if (onFieldChange) {
            onFieldChange(fieldKey, value, formData);
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        fields.forEach(field => {
            if (field.required && (!formData[field.key] || formData[field.key].toString().trim() === '')) {
                newErrors[field.key] = `${field.label} is required`;
            }

            // Custom validation
            if (field.validate && formData[field.key]) {
                const validationResult = field.validate(formData[field.key], formData);
                if (validationResult !== true) {
                    newErrors[field.key] = validationResult;
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = () => {
        if (validateForm()) {
            if (onSubmit) {
                onSubmit(formData);
            }
        }
    };

    // Handle button click
    const handleButtonClick = (button) => {
        switch (button.action) {
            case 'submit':
                handleSubmit();
                break;
            case 'close':
                if (onCancel) {
                    onCancel();
                } else {
                    onHide();
                }
                break;
            case 'custom':
                if (button.onClick) {
                    button.onClick(formData);
                }
                break;
            default:
                if (button.onClick) {
                    button.onClick(formData);
                }
        }
    };

    // Render form field
    const renderField = (field) => {
        const {
            key,
            label,
            type = 'text',
            placeholder = '',
            options = [],
            rows = 3,
            required = false,
            disabled = false,
            accept,
            step,
            min,
            max,
            className = ""
        } = field;

        const fieldValue = formData[key] || '';
        const hasError = errors[key];

        const commonProps = {
            value: fieldValue,
            onChange: (e) => handleInputChange(key, e.target.value),
            disabled: disabled || loading,
            className: `${hasError ? 'is-invalid' : ''} ${className}`,
            placeholder
        };

        let fieldElement;

        switch (type) {
            case 'select':
                fieldElement = (
                    <Form.Select {...commonProps}>
                        <option value="">Select {label}...</option>
                        {options.map((option, index) => (
                            <option key={index} value={option.value || option}>
                                {option.label || option}
                            </option>
                        ))}
                    </Form.Select>
                );
                break;

            case 'textarea':
                fieldElement = (
                    <Form.Control
                        as="textarea"
                        rows={rows}
                        {...commonProps}
                    />
                );
                break;

            case 'file':
                fieldElement = (
                    <Form.Control
                        type="file"
                        accept={accept}
                        onChange={(e) => handleInputChange(key, e.target.files[0])}
                        disabled={disabled || loading}
                        className={hasError ? 'is-invalid' : ''}
                    />
                );
                break;

            case 'checkbox':
                fieldElement = (
                    <Form.Check
                        type="checkbox"
                        checked={fieldValue === true || fieldValue === 'true'}
                        onChange={(e) => handleInputChange(key, e.target.checked)}
                        disabled={disabled || loading}
                        label={placeholder}
                    />
                );
                break;

            case 'radio':
                fieldElement = (
                    <div>
                        {options.map((option, index) => (
                            <Form.Check
                                key={index}
                                type="radio"
                                name={key}
                                id={`${key}-${index}`}
                                label={option.label || option}
                                value={option.value || option}
                                checked={fieldValue === (option.value || option)}
                                onChange={(e) => handleInputChange(key, e.target.value)}
                                disabled={disabled || loading}
                            />
                        ))}
                    </div>
                );
                break;

            default:
                fieldElement = (
                    <Form.Control
                        type={type}
                        step={step}
                        min={min}
                        max={max}
                        {...commonProps}
                    />
                );
        }

        return (
            <Col xs={12} sm={field.colSm || 12} md={field.colMd || 12} lg={field.colLg || 12} className="mb-3" key={key}>
                {type !== 'checkbox' && (
                    <Form.Label className="fw-medium">
                        {label} {required && <span className="text-danger">*</span>}
                    </Form.Label>
                )}
                {fieldElement}
                {hasError && (
                    <Form.Text className="text-danger">
                        {hasError}
                    </Form.Text>
                )}
                {field.helpText && (
                    <Form.Text className="text-muted">
                        {field.helpText}
                    </Form.Text>
                )}
            </Col>
        );
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size={size}
            centered={centered}
            backdrop={backdrop}
            keyboard={keyboard && !loading}
            className='border-0'
            style={{ zIndex: 9999 }} 
        >
            <Modal.Header closeButton={!loading} className={headerClassName}>
                <Modal.Title className='fw-bold'>{title}</Modal.Title>
            </Modal.Header>

            <Modal.Body className={bodyClassName}>
                {children ? (
                    children
                ) : (
                    fields.length > 0 && (
                        <Form>
                            <Row>
                                {fields.map(renderField)}
                            </Row>
                        </Form>
                    )
                )}
            </Modal.Body>

            {buttons.length > 0 && (
                <Modal.Footer className={footerClassName}>
                    {buttons.map((button, index) => (
                        <Button
                            key={index}
                            variant={button.variant || 'secondary'}
                            size={button.size || 'sm'}
                            disabled={button.disabled || loading}
                            onClick={() => handleButtonClick(button)}
                            className={button.className || ''}
                            style={{ width: '100px', fontWeight: "bold" }}

                        >
                            {loading && button.action === 'submit' ? (
                                <>
                                    {button.loadingLabel || 'Loading...'}
                                </>
                            ) : (
                                button.label
                            )}
                        </Button>
                    ))}
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default DynamicModal;